import { ref, computed } from 'vue'

export interface PaletteCommand {
  id: string
  label: string
  category: 'navigation' | 'content' | 'action'
  icon?: string
  keywords?: string[]
  shortcut?: string
  execute: () => void
}

/**
 * Module-level singleton state — shared across all callers of useCommandPalette()
 * so that every component can register/execute commands against the same palette.
 */
const isOpen = ref(false)
const query = ref('')
const registeredCommands = ref<PaletteCommand[]>([])

function loadRecents(): string[] {
  try {
    return JSON.parse(localStorage.getItem('relay-command-palette-recent') || '[]')
  } catch {
    return []
  }
}

const recentCommandIds = ref<string[]>(loadRecents())

function saveRecents() {
  localStorage.setItem('relay-command-palette-recent', JSON.stringify(recentCommandIds.value))
}

// Fuzzy match: every query token must appear as substring in searchable text
function matchesQuery(cmd: PaletteCommand, q: string): { matches: boolean; score: number } {
  if (!q.trim()) return { matches: true, score: 0 }

  const tokens = q.toLowerCase().split(/\s+/).filter(Boolean)
  const labelLower = cmd.label.toLowerCase()
  const searchText = (cmd.label + ' ' + (cmd.keywords || []).join(' ')).toLowerCase()

  const allMatch = tokens.every(t => searchText.includes(t))
  if (!allMatch) return { matches: false, score: 0 }

  // Score: prefix match on label > substring in label > keyword-only match
  let score = 0
  const labelMatchesAll = tokens.every(t => labelLower.includes(t))
  const prefixMatch = tokens.some(t => labelLower.startsWith(t))

  if (prefixMatch) score = 3
  else if (labelMatchesAll) score = 2
  else score = 1

  return { matches: true, score }
}

const filteredCommands = computed(() => {
  const q = query.value.trim()

  if (!q) {
    // Show recents first, then all navigation commands
    const recents = recentCommandIds.value
      .map(id => registeredCommands.value.find(c => c.id === id))
      .filter((c): c is PaletteCommand => !!c)

    const navCommands = registeredCommands.value.filter(
      c => c.category === 'navigation' && !recentCommandIds.value.includes(c.id)
    )

    const actionCommands = registeredCommands.value.filter(
      c => c.category === 'action' && !recentCommandIds.value.includes(c.id)
    )

    return [...recents, ...navCommands, ...actionCommands]
  }

  // Filter and sort by score
  return registeredCommands.value
    .map(cmd => ({ cmd, ...matchesQuery(cmd, q) }))
    .filter(r => r.matches)
    .sort((a, b) => b.score - a.score)
    .map(r => r.cmd)
})

function open() {
  query.value = ''
  isOpen.value = true
}

function close() {
  isOpen.value = false
}

function toggle() {
  if (isOpen.value) close()
  else open()
}

function registerCommands(commands: PaletteCommand[]) {
  for (const cmd of commands) {
    const existing = registeredCommands.value.findIndex(c => c.id === cmd.id)
    if (existing >= 0) {
      registeredCommands.value[existing] = cmd
    } else {
      registeredCommands.value.push(cmd)
    }
  }
}

function unregisterCommands(ids: string[]) {
  registeredCommands.value = registeredCommands.value.filter(c => !ids.includes(c.id))
}

function deregisterCommandsByPrefix(prefix: string) {
  registeredCommands.value = registeredCommands.value.filter(c => !c.id.startsWith(prefix))
}

function executeCommand(cmd: PaletteCommand) {
  cmd.execute()

  // Update recents (keep last 5)
  recentCommandIds.value = [
    cmd.id,
    ...recentCommandIds.value.filter(id => id !== cmd.id)
  ].slice(0, 5)
  saveRecents()

  close()
}

// Global keyboard shortcut
function handleGlobalKeydown(e: KeyboardEvent) {
  const isMeta = e.metaKey || e.ctrlKey
  if (isMeta && e.key === 'k') {
    e.preventDefault()
    toggle()
  }
}

function setupKeyboardShortcut() {
  document.addEventListener('keydown', handleGlobalKeydown)
}

function teardownKeyboardShortcut() {
  document.removeEventListener('keydown', handleGlobalKeydown)
}

/** Resets all state to defaults. Useful in tests to avoid cross-test bleed. */
function $reset() {
  isOpen.value = false
  query.value = ''
  registeredCommands.value = []
  recentCommandIds.value = []
}

export function useCommandPalette() {
  return {
    isOpen,
    query,
    filteredCommands,
    open,
    close,
    toggle,
    registerCommands,
    unregisterCommands,
    deregisterCommandsByPrefix,
    executeCommand,
    setupKeyboardShortcut,
    teardownKeyboardShortcut,
    $reset
  }
}
